import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import StorefrontSection from '../components/storefront/StorefrontSection';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
/* @ts-expect-error module is untyped */

import ThemeBusinessTypesGrid from '../components/storefront/ThemeBusinessTypesGrid';
/* @ts-expect-error module is untyped */

import WelcomeBanner from '../components/storefront/WelcomeBanner';
import { selectDisplayedCollections } from '../slices/collections/collectionsSlice';
import { PublicAppContext } from '../selectors/app';
import CalloutsSection from '../components/storefront/home/CalloutsSection';
import CollectionSection from '../components/storefront/home/CollectionSection';
import CollectionRouter from '../components/storefront/home/CollectionRouter';
import { useCanonicalUrl } from '../hooks/useCanonicalUrl';
import { useSocialScreenshotUrl } from '../hooks/useSocialScreenshotUrl';
import { getThemesStorefrontRoute } from '../utils/urls';
const DEFAULT_EVENT_PROPERTIES = {
  screen: 'storefront home',
  type: 'theme'
};
const SECTION_STYLES = `p-top-8 p-bottom-0`;

const StorefrontHomeContainer = () => {
  const [pageviewReady, setPageviewReady] = useState(false);
  const displayedCollections = useSelector(selectDisplayedCollections);
  const {
    trackPageview,
    trackInteraction,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const trackBusinessTypesInteraction = useCallback((action, properties = {}) => {
    trackInteraction(action, Object.assign({}, properties, {
      section: 'section::business-types::static',
      type: 'theme'
    }));
  }, [trackInteraction]);
  const canonicalUrl = useCanonicalUrl();
  const defaultMetaDescription = unescapedText('marketplace-assets-ui.themes.metaDescription.defaultMetaDescription');
  useEffect(() => {
    document.title = I18n.text('marketplace-assets-ui.htmlTitle');
  }, []);
  useEffect(() => {
    if (pageviewReady) {
      trackPageview();
    } else {
      setDefaultEventProperties(DEFAULT_EVENT_PROPERTIES);
      setPageviewReady(true);
    }
  }, [pageviewReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pageviewReady) {
      return () => setDefaultEventProperties(null);
    }

    return () => {};
  }, [pageviewReady, setDefaultEventProperties]);
  const socialScreenshotUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "STOREFRONT_HOME_LOAD",
    children: [/*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx(PublicAppContext, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MetaDescription, {
            content: defaultMetaDescription
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "card"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "site"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "type"
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "url",
            content: canonicalUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "title",
            content: unescapedText('marketplace-assets-ui.htmlTitle')
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "description",
            content: defaultMetaDescription
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "image",
            content: socialScreenshotUrl
          })]
        })
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      responsive: true,
      children: /*#__PURE__*/_jsxs(MainSectionInner, {
        children: [/*#__PURE__*/_jsx(CalloutsSection, {
          className: SECTION_STYLES
        }), displayedCollections.map(collection => collection.publishedListing && /*#__PURE__*/_jsx(CollectionSection, {
          className: SECTION_STYLES,
          collectionId: collection.id,
          collectionTagline: collection.publishedListing.tagline,
          collectionTitle: collection.publishedListing.title,
          collectionType: "ranked",
          linkTo: getThemesStorefrontRoute(collection.publishedListing.slug),
          trackInteraction: trackInteraction,
          trackPageview: trackPageview
        }, collection.id))]
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      color: "light",
      responsive: true,
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsx(StorefrontSection, {
          className: SECTION_STYLES,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.allBusinessTypes.homeTitle"
          }),
          titleControls: /*#__PURE__*/_jsx(CollectionRouter, {
            linkTo: getThemesStorefrontRoute('categories'),
            linkText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.themes.allBusinessTypes.homeLinkText"
            }),
            trackInteraction: trackInteraction
          }),
          children: /*#__PURE__*/_jsx(ThemeBusinessTypesGrid, {
            limit: 6,
            onTrackInteraction: trackBusinessTypesInteraction
          })
        })
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      responsive: true,
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsx(WelcomeBanner, {
          onTrackInteraction: trackInteraction
        })
      })
    })]
  });
};

export default StorefrontHomeContainer;